import React, { useState, useEffect } from 'react';
import {
    Modal,
    ModalHeader,
    ModalBody,
    Form,
    FormGroup,
    Label,
    Row,
    Col,
    Container,
    Alert
} from 'reactstrap';
import RenderHierarchyTree from '../../../components/RenderHierarchyTree/RenderHierarchyTree';
import SearchInput from '../../../components/Search/SearchInput';
import './CreateUpdateUserModal.css';
import RenderRoles from '../../../components/RenderRoles/RenderRoles';
import SingleUserSection from './UserSections/SingleUserSection';
import AccountTypeAndStatus from './UserSections/AccountTypeAndStatus';
import UserPreferencesSection from './UserSections/UserPreferencesSection';
import SharedUserSection from './UserSections/SharedUserSection';
import { OrganizationalRoleType } from '../../../constants/OrganizationalRoleType';
import { UserAccountType } from '../../../constants/UserAccountType';
import { EMAIL_REGEX } from '../../../constants/regex';
import { useInsertTenantUserMutation, useUpdateTenantUserMutation } from '../../../utilities/mutations/useInsertTenantUserMutation';
import { useGetTenantUserByIdQuery, useGetTenantUserByEmailQuery } from '../../../utilities/queries/useGetTenantUsersQuery';
import { makeToast } from '../../../utilities/appStore';
import { locationModalTypes } from '../../../constants/treeState';
import { successToast, errorToast } from '../../../constants/toast';
import UserLanguagePreference from '../../../components/Enums/UserLanguagePreference';
import Button from '../../../components/UIButton/UIButton';
import ActionModalHeader from '../../../components/ActionModalHeader/ActionModalHeader';
import Spinner from '../../../components/UISpinner/UISpinner';
import { AccountIdType } from '../../../constants/AccountIdType';

const emptyModalData = {
    accountId: "",
    accountType: UserAccountType.Single,
    userData: {
        fName: "",
        lName: "",
        email: "",
        languagePreference: UserLanguagePreference.Unknown,
        userName: "",
        password: "",
        hasPassword: false
    },
    selectedRole: "",
    userStatus: "",
    hierarchyData: {
        selectedNodes: []
    }
}

const CreateUpdateUserModal = ({ isOpen, toggle, modalEditId, doesEmailExistInUserData, openRemoveUserModal = () => { } }) => {
    const [modalData, setModalData] = useState(emptyModalData);
    const [initialModalData, setInitialModalData] = useState(null);
    const [roleSearchTerm, setRoleSearchTerm] = useState("");
    const [validationErrors, setValidationErrors] = useState({});
    const [emailToCheck, setEmailToCheck] = useState(null);
    const [isEmailInUse, setIsEmailInUse] = useState();
    const { isPending: isInsertLoading, mutateAsync: insertAsync, error: insertError, reset: insertReset } = useInsertTenantUserMutation();
    const { isPending: isUpdateLoading, mutateAsync: updateAsync, error: updateError, reset: updateReset } = useUpdateTenantUserMutation();
    const { data: fetchedData, error: fetchUserError, isLoading: isFetchLoading } = useGetTenantUserByIdQuery(modalEditId);

    const fakePassword = "jebpazrb";
    const { data: fetchedEmailData, error: fetchUserEmailError, isLoading: isEmailFetchLoading } = useGetTenantUserByEmailQuery(emailToCheck);

    useEffect(() => {
        if (fetchedData) {
            const formattedData = toFormData(fetchedData);
            setModalData(formattedData);
            setInitialModalData(structuredClone(formattedData));
        }

        if (!emailToCheck) return;

        if (fetchedEmailData && fetchedEmailData.email === emailToCheck) {
            setIsEmailInUse(true);
            setModalData({ ...modalData, selectedRole: OrganizationalRoleType[fetchedEmailData.roleName] });
        } else if (fetchedEmailData && fetchedEmailData.email !== emailToCheck) {
            setValidationErrors((prev) => ({
                ...prev,
                email: null,
            }));
            setIsEmailInUse(false);
        } else {
            setValidationErrors((prev) => ({
                ...prev,
                email: null,
            }));
            setIsEmailInUse(null);
        }
    }, [fetchedEmailData, fetchUserEmailError, emailToCheck, fetchedData]);

    const didSelectedNodesChange = () => {
        const current = modalData.hierarchyData.selectedNodes || [];
        const initial = initialModalData?.hierarchyData?.selectedNodes || [];

        if (current.length !== initial.length) return true;

        const currentSorted = [...current].sort();
        const initialSorted = [...initial].sort();

        return !currentSorted.every((val, idx) => val === initialSorted[idx]);
    };

    const didLanguagePreferenceChange = () => {
        const current = modalData.userData.languagePreference;
        const initial = initialModalData?.userData?.languagePreference;
        return current !== initial;
    };

    const successCall = () => {
        const isShared = modalData.accountType === UserAccountType.Shared;
        if (modalType === locationModalTypes.Add) {
            if (isShared) {
                makeToast("Shared User Created", successToast)
            }
            else {
                makeToast("User Invite Sent", successToast)
            }
        }
        else makeToast("User updated", successToast);
        handleToggleModal();
    };
    const handleCancelButtonClick = () => {
        insertReset();
        updateReset();
        setEmailToCheck(null);
        handleToggleModal();
        setRoleChangeTick(0);
    }

    const failedCall = () => {
        const isShared = modalData.accountType === UserAccountType.Shared;
        if (modalType === locationModalTypes.Add) {
            if (isShared) {
                makeToast("Error Creating Shared User", errorToast)
            }
            else {
                makeToast("Error Sending User Invite", errorToast)
            }
        }
        else makeToast("Error updating User", errorToast);
    };

    const handleToggleModal = () => {
        toggle();
        setModalData(emptyModalData);
        setValidationErrors({});
    }
    const setSelectedNodes = (nodes) => {
        setModalData((prev) => (
            {
                ...prev,
                hierarchyData: {
                    ...prev.hierarchyData,
                    selectedNodes: nodes
                }
            }
        ))
        setValidationErrors((prev) => (
            {
                ...prev,
                hierarchyData: null
            }
        ))
    };
    const handleChange = (e) => {
        const { name: fieldName, value: fieldValue, checked } = e.target;

        setModalData((prev) => ({
            ...prev,
            userData: {
                ...prev.userData,
                [fieldName]: getFieldValue(fieldName, fieldValue, checked)
            }
        }));

        setValidationErrors((prev) => ({
            ...prev,
            [fieldName]: null
        }));
    };

    const getFieldValue = (fieldName, fieldValue, checked) => {
        if (fieldName === "languagePreference") {
            return Number(fieldValue);
        } else if (fieldName === "hasPassword") {
            return checked;
        } else {
            return fieldValue;
        }
    }


    const handleAccountTypeSwitch = (accountType) => {
        if (accountType === modalData.accountType) return; //Selection didn't change
        setModalData((prev) => {
            let selectedRole = "";
            if (accountType === UserAccountType.Shared) {
                selectedRole = OrganizationalRoleType.AppUser;
            }
            return {
                ...prev,
                accountType,
                selectedRole
            }
        });
        setValidationErrors({});
    }
    const [roleChangeTick, setRoleChangeTick] = useState(0);

    const handleRoleSelect = (role) => {
        setModalData((prev) => {
            const next = { ...prev, selectedRole: role };

            // user picked PortalAdmin (create OR edit) and it’s a change
            const wasPortalAdmin = prev.selectedRole === OrganizationalRoleType.PortalAdmin;
            const isPortalAdmin = role === OrganizationalRoleType.PortalAdmin;

            if (isPortalAdmin && !wasPortalAdmin) {
                setRoleChangeTick((t) => t + 1);
            }

            return next;
        });

        setValidationErrors((prev) => ({
            ...prev,
            selectedRole: null,
        }));
    };

    function validatePassword(password, hasPassword) {
        //no password is a valid password
        if (!hasPassword || (modalType === locationModalTypes.Edit && !isHasPasswordDirty && (password === '' || password === fakePassword))) {
            return null;
        }
        else if (password == null || password === '') {
            return 'Password is required when requiring a password.'
        }


        const isMinLength = password.length >= 8;
        const overMaxLength = password.length > 100;
        const hasNumber = /\d/.test(password);
        const hasUppercase = /[A-Z]/.test(password);
        const hasLowercase = /[a-z]/.test(password);
        const hasSpecialChar = /[^A-Za-z0-9]/.test(password);
        const rulesPassed = [hasNumber, hasUppercase, hasLowercase, hasSpecialChar]
            .filter(Boolean).length;

        if (password?.length === 0) {
            return null;
        }

        

        if (!isMinLength) {
            return "Password must be at least 8 characters long.";
        }

        if (overMaxLength) {
            return "Password may only contain a maximum of 100 characters."
        }

        if (rulesPassed < 2) {
            return "Password must be a minimum of 8 characters and use two of the following: number, lowercase letter, capital letter, or special character.";
        }

        return null; // valid
    }

    const handlePasswordBlur = (e) => {
        const msg = validatePassword(e.target.value, true);
        setValidationErrors((prev) => ({
            ...prev,
            password: msg
        }));
    };

    const handlePasswordFocus = (e) => {
        
        if (e.target.value === fakePassword) {
            setModalData((prev) => ({
                ...prev,
                userData: {
                    ...prev.userData,
                    password: ''
                }
            }));
        }
    };

    const handleEmailBlur = (e) => {
        const email = e.target.value;
        const errors = {};

        validateEmail(errors, email);
        setValidationErrors(errors);

        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }

        else {
            setEmailToCheck(email);
        }
    }
    function toUserAccountModel(data) {
        return {
            Name: `${data.userData.fName} ${data.userData.lName}`.trim(),
            Email: data.userData.email,
            AssignedRole: data.selectedRole,
            HierarchyAssignment: data.hierarchyData.selectedNodes.filter(id => id !== AccountIdType.GhostAccountId).map(Number),
            AppPassword: data.userData.hasPassword ? data.userData.password : null,
            Username: data.userData.userName,
            AccountType: modalData.accountType,
            LanguagePreference: data.userData.languagePreference,
            hasPassword: data.userData.hasPassword
        };
    }
    function toUserUpdateModel(data) {
        return {
            Name: UserAccountType.Single === modalData.accountType ? `${data.userData.fName} ${data.userData.lName}`.trim() : data.userData.userName,
            Email: data.userData.email,
            AssignedRoleChanged: modalData.selectedRole !== initialModalData.selectedRole,
            AssignedRole: data.selectedRole || null,
            HierarchyAssignments: data.hierarchyData.selectedNodes.filter(id => id !== AccountIdType.GhostAccountId).map(Number),
            HierarchyAssignmentsChanged: didSelectedNodesChange(),
            AccountId: data.accountId,
            LanguagePreference: data.userData.languagePreference ?? UserLanguagePreference.Unknown,
            PreferredLanguageChanged: didLanguagePreferenceChange(),
            UserType: modalData.accountType,
            Password: data.userData.hasPassword && data.userData.password !== fakePassword ? data.userData.password : null,
            hasPassword: data.userData.hasPassword
        };
    }
    function toFormData(model) {
        const [fName, ...rest] = model.name.split(" ");
        const lName = rest.join(" ");

        return {
            ...modalData,
            accountId: model.accountId,
            accountType: model.userType,
            userStatus: model.userStatus,          
            userData: {
                fName: fName || "",
                lName: lName || "",
                email: model.email || "",
                languagePreference: model.languagePreference || UserLanguagePreference.Unknown,
                userName: model.name,
                hasPassword: model.hasPassword || false,
                password: model.hasPassword ? fakePassword : null
            },
            selectedRole: OrganizationalRoleType[model.roleName] ?? "",
            hierarchyData: {
                selectedNodes: model.hierarchyAssignments.map(String)
            }
        };
    }

    const validateUserForm = (modalData) => {
        const errors = {};

        if (modalData.accountType === UserAccountType.Single) {
            const { fName, lName, email } = modalData.userData;

            if (!fName.trim()) errors.fName = "First name is required";
            if (!lName.trim()) errors.lName = "Last name is required";

            const fullNameLength = (fName + lName).trim().length;
            if (fullNameLength >= 100) {
                errors.fName = "First and Last name combined must be less than 100 characters";
            }

            validateEmail(errors, modalData.userData.email);
        }

        if (modalData.accountType === UserAccountType.Shared) {
            const { userName, password, hasPassword } = modalData.userData;

            if (!userName.trim()) {
                errors.userName = "User name is required";
            } else if (userName.length >= 80) {
                errors.userName = "Username must be less than 80 characters";
            }

            const msg = validatePassword(password, hasPassword);
            if (msg) {
                errors.password = msg;
            }
        }
        if (!modalData.selectedRole) {
            errors.selectedRole = true;
        }

        if (modalData.hierarchyData.selectedNodes.length === 0) {
            errors.hierarchyData = "Please make a selection";
        }

        return errors;
    };

    const validateEmail = (errors, email) => {
        if (!email.trim()) {
            errors.email = "Email is required";
        } else if (!EMAIL_REGEX.test(email)) {
            errors.email = "Invalid email format";
        } else if (email.length >= 100) {
            errors.email = "Email must be less than 100 characters";
        }
    }


    const handleUpdateUser = async () => {
        const errors = validateUserForm(modalData);

        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }

        setValidationErrors({});
        await updateAsync({ userAccountUpdateModel: toUserUpdateModel(modalData) }, { onError: failedCall, onSuccess: successCall });
    };

    const handleSubmit = async () => {
        const baseErrors = validateUserForm(modalData);
        const errors = { ...baseErrors };

        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }

        setValidationErrors({});
        await insertAsync({ userAccountModel: toUserAccountModel(modalData) }, { onError: failedCall, onSuccess: successCall });
    };

    const getLoadingMessage = () => {
        if (isInsertLoading) return "Creating user...";
        if (isFetchLoading) return "Fetching users...";
        if (isUpdateLoading) return "Updating user...";
        return "";
    };
    const isLoading = isInsertLoading || isFetchLoading || isUpdateLoading;
    const modalType = modalEditId ? locationModalTypes.Edit : locationModalTypes.Add;
    const isHasPasswordDirty = modalType === locationModalTypes.Edit &&
        initialModalData?.userData?.hasPassword != null &&
        modalData?.userData?.hasPassword != null &&
        initialModalData?.userData?.hasPassword !== modalData.userData.hasPassword;
    const modalHeaderText = modalType === locationModalTypes.Edit ? 'Edit User' :
        modalData.accountType === UserAccountType.Single ?
        'Invite New User' :
        'Create Shared User';
    const saveOrUpdateButton =
        modalType === locationModalTypes.Edit ? (
            <Button variant="submit" size="medium" onClick={handleUpdateUser} disabled={!!fetchUserError || isLoading}>
                Update User
            </Button>
        ) : (
                <Button variant="submit" size="medium" onClick={handleSubmit} disabled={isLoading || isEmailFetchLoading}>
                Save User
            </Button>
        );
    return (
        <Modal isOpen={isOpen} toggle={handleToggleModal} backdrop="static" size="xl" className="custom-modal-width-80p">
            <ActionModalHeader
                title={modalHeaderText}
                onCancel={handleCancelButtonClick}
                cancelButtonDisabled={isLoading}
                actionButton={saveOrUpdateButton}
            />
            <ModalBody className="p-0">
                {isLoading && <Spinner text={getLoadingMessage()} />}
                {fetchUserError && (
                    <Alert color="danger" className="mb-3">
                        Failed to load user data. Please try again.
                    </Alert>
                )}
                {!fetchUserError && !isLoading && <Form data-testid="modal-form" className="m-0 p-0">
                    <AccountTypeAndStatus
                        accountSwitch={handleAccountTypeSwitch}
                        modalData={modalData}
                        modalType={modalType}
                        openRemoveUserModal={openRemoveUserModal}
                        toggleParentModal={handleToggleModal}
                    />
                    {/* User Info */}
                    <Container style={{ fontSize: '0.875rem' }} className="pl-3 pt-0 pb-0 m-1">
                        <Row className="pb-0" style={{ marginBottom: "-1rem" }}>
                            <Col md="6" className="pl-4">
                                {modalData.accountType === UserAccountType.Single && <SingleUserSection
                                    modalData={modalData}
                                    handleChange={handleChange}
                                    handleEmailBlur={handleEmailBlur}
                                    validationErrors={validationErrors}
                                    disableEmail={modalType === locationModalTypes.Edit}
                                    isEmailInUse={isEmailInUse}
                                    isEmailFetchLoading={isEmailFetchLoading}
                                />}
                                {modalData.accountType === UserAccountType.Shared && <SharedUserSection
                                    modalData={modalData}
                                    handleChange={handleChange}
                                    validationErrors={validationErrors}
                                    handlePasswordBlur={handlePasswordBlur}
                                    onPasswordFocus={handlePasswordFocus}
                                />}
                            </Col>
                            <Col md="6">
                                <UserPreferencesSection
                                    modalData={modalData}
                                    handleChange={handleChange}
                                />
                            </Col>
                        </Row>
                        <hr className="my-3" />




                        {/* Roles and Hierarchy */}
                        <Row>
                            <Col md="4" className="pl-4">
                                <Label>Assign Permission Roles *</Label>
                                <div className={`small mb-2 ${validationErrors.selectedRole ? 'text-danger' : 'text-muted'}`} >
                                    Select a role for this user.
                                </div>
                                <div className="border min-350-h">
                                    <SearchInput
                                        value={roleSearchTerm}
                                        onChange={(e) => setRoleSearchTerm(e.target.value)}
                                        style={{ height: "30px" }}
                                        noRounded={true}
                                    />
                                    <RenderRoles
                                        roleSearchTerm={roleSearchTerm}
                                        handleRoleSelect={handleRoleSelect}
                                        selectedRole={modalData.selectedRole}
                                        includeOnly={modalData.accountType === UserAccountType.Shared ? [OrganizationalRoleType.AppUser] : []}
                                        disableRoleSelection={isEmailInUse}
                                    />
                                </div>

                            </Col>

                            <Col md="8" className="pr-4">
                                <FormGroup row className="align-items-center">
                                    <Col sm={12}>
                                        <Label>Assign to Hierarchy *</Label>
                                        {validationErrors.hierarchyData && (
                                            <div className="text-danger small mt-0">{validationErrors.hierarchyData}</div>
                                        )}
                                        <div className="text-muted small mb-2">
                                            *Notice: User will have access to all sub levels from where they are assigned.
                                        </div>
                                        <div className="mini-tree-container min-350-h tree-bg">
                                            <RenderHierarchyTree
                                                checkedItems={modalData.hierarchyData.selectedNodes}
                                                setCheckedItems={setSelectedNodes}
                                                treeButton="deselectAll"
                                                treeMaxHeight="40vh"
                                                disableAllCheckboxes={modalData.selectedRole === OrganizationalRoleType.PortalAdmin}
                                                selectAllTrigger={roleChangeTick}
                                            />
                                        </div>
                                    </Col>
                                </FormGroup>
                            </Col>
                        </Row>
                    </Container>
                </Form>}
            </ModalBody>
        </Modal>
    );
};

export default CreateUpdateUserModal;
